import { Box, Button } from "@mui/material";
import axios from "axios";
import { ChangeEvent, useState } from "react";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { VisuallyHiddenInput } from "@/components/VisuallyHiddenInput";
import { useSnackbar } from "@/hooks/useSnackbar";
import { BASE_PATH } from "@/services/base";
import { getConfigObject, getErrorSnackbar, getXtenantId } from "@/utils/utils";
import {
  MINI_COG_IMAGE_ACCEPT,
  MINI_COG_IMAGE_BOX_HEIGHT_PX,
  MINI_COG_IMAGE_BOX_WIDTH_PX,
  MINI_COG_IMAGE_MAX_BYTES,
  MINI_COG_IMAGE_MIME_TYPES,
  MINI_COG_TEXT,
} from "../initial-f2f-mini-cog-constant";

export interface MiniCogClockUploadProps {
  /** S3 key saved in the note. */
  image: string;
  onChange: (image: string) => void;
  appointmentId?: string;
  /** Presigned URL of the saved drawing, from the encounter snapshot. */
  savedImageUrl?: string;
}

const MiniCogClockUpload = ({
  image,
  onChange,
  appointmentId,
  savedImageUrl,
}: MiniCogClockUploadProps) => {
  const showSnackbar = useSnackbar();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>();
  const imageUrl = image ? (uploadedImageUrl ?? savedImageUrl) : undefined;

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.item(0);
    event.target.value = "";
    if (!file) return;
    if (!MINI_COG_IMAGE_MIME_TYPES.includes(file.type)) {
      showSnackbar({ message: MINI_COG_TEXT.imageTypeError, type: "error" });
      return;
    }
    if (file.size > MINI_COG_IMAGE_MAX_BYTES) {
      showSnackbar({ message: MINI_COG_TEXT.imageTooLarge, type: "error" });
      return;
    }
    const xTenantId = getXtenantId();
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(
        `${BASE_PATH}/api/master/encounter-summary/clock-drawing`,
        formData,
        {
          params: { appointmentUuid: appointmentId },
          headers: {
            Authorization: `Bearer ${getConfigObject().accessToken}`,
            ...(xTenantId && { "X-TENANT-ID": xTenantId }),
          },
        }
      );
      const { key, url } = res.data?.data ?? {};
      setUploadedImageUrl(url);
      onChange(key);
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Text size={13}>{MINI_COG_TEXT.clockImageLabel}</Text>
      <Box
        position="relative"
        sx={{
          width: MINI_COG_IMAGE_BOX_WIDTH_PX,
          maxWidth: "100%",
          height: MINI_COG_IMAGE_BOX_HEIGHT_PX,
          border: "1px solid gray",
          borderRadius: "5px",
          overflow: "hidden",
        }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={MINI_COG_TEXT.clockImageAlt}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        )}
        <Flex position="absolute" bottom={10} right={10} gap={1}>
          <Button
            component="label"
            variant="outlined"
            disabled={!appointmentId}
            sx={{ height: "24px", bgcolor: "white" }}
          >
            {MINI_COG_TEXT.uploadImage}
            <VisuallyHiddenInput
              type="file"
              accept={MINI_COG_IMAGE_ACCEPT}
              onChange={handleUpload}
            />
          </Button>
          <Button
            variant="contained"
            sx={{ height: "24px" }}
            disabled={!image}
            onClick={() => {
              setUploadedImageUrl(undefined);
              onChange("");
            }}
          >
            {MINI_COG_TEXT.clearImage}
          </Button>
        </Flex>
      </Box>
      <Text size={13} c="text.secondary">
        {MINI_COG_TEXT.uploadHelper}
      </Text>
    </Box>
  );
};

export default MiniCogClockUpload;
